import { useState, useEffect, useCallback, useRef } from 'react'
import { isRequestCanceled } from '../services/core/errorHandler'

interface UseDataFetchingOptions<T> {
  /** Function to fetch data */
  fetchFn: (params?: any) => Promise<T>
  /** Dependencies that trigger refetch */
  dependencies?: any[]
  /** Whether to fetch immediately on mount */
  immediate?: boolean
  /** Transform response before setting data */
  transform?: (response: T) => T
  /** Error handler */
  onError?: (error: any) => void
  /** Success handler */
  onSuccess?: (data: T) => void
}

interface UseDataFetchingReturn<T> {
  /** The fetched data */
  data: T | null
  /** Loading state */
  loading: boolean
  /** Error state */
  error: string | null
  /** Manual refetch function */
  refetch: (params?: any) => Promise<void>
  /** Set data manually */
  setData: (data: T | null) => void
  /** Set loading state manually */
  setLoading: (loading: boolean) => void
  /** Set error state manually */
  setError: (error: string | null) => void
}

export function useDataFetching<T = any>({
  fetchFn,
  dependencies = [],
  immediate = true,
  transform,
  onError,
  onSuccess
}: UseDataFetchingOptions<T>): UseDataFetchingReturn<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Ref to track abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Ref to track if component is mounted
  const mountedRef = useRef(true)
  
  const refetch = useCallback(async (params?: any) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    // Create new abort controller
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetchFn(params)
      
      // Check if request was cancelled or component unmounted
      if (abortController.signal.aborted || !mountedRef.current) {
        return
      }
      
      const transformedData = transform ? transform(response) : response
      setData(transformedData)
      
      if (onSuccess) {
        onSuccess(transformedData)
      }
      
    } catch (err: any) {
      // Ignore abort errors
      if (isRequestCanceled(err) || !mountedRef.current) {
        return
      }
      
      console.error('Data fetching error:', err)
      
      let errorMessage = 'An error occurred while fetching data'
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
      
      if (onError) {
        onError(err)
      }
      
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
      
      // Clear the abort controller reference
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }, [fetchFn, transform, onError, onSuccess])

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (immediate) {
      refetch()
    }
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, dependencies) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true
    
    return () => {
      mountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    data,
    loading,
    error,
    refetch,
    setData,
    setLoading,
    setError
  }
}

export default useDataFetching